/**
 * AccountEditSheet: Tabbio settings sheet for name / username / email / password.
 *
 * Shared between native and web (both `.tsx` and `.web.tsx` re-export from
 * here). Rendered as a form-sheet on iOS, a card screen on Android, and an
 * embedded panel inside the desktop settings dialog.
 *
 * Name and username use a plain input and save button. Email and password match
 * the profile sheets:
 *   • Flat tinted cards (`theme.surface`), 18-radius, no borders.
 *   • Neutral icon disc per section (lock / mail / key).
 *   • White pill inputs (52h, subtle border + shadow).
 *   • Solid neutral primary CTAs (`theme.text` bg, `theme.background` text).
 *   • Outline CTAs in tinted secondary surface for "send OTP" style actions.
 *   • Status text inline, color-tinted (success #12a150, error #d93025).
 */
import React, { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, type TextInputProps, View, type ViewStyle, type StyleProp } from 'react-native';
import { Text, TextInput } from '@/components/common/AppText';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { Lock, Mail } from '@/components/icons/HugeIcons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { useHaptics } from '../../hooks/useHaptics';
import { useAccountEditor } from '../../hooks/useAccountEditor';
import { confirmUsernameChange } from '../../lib/username-change-confirmation';
import { PasswordField } from '../common/PasswordField';

interface AccountEditSheetProps {
  embedded?: boolean;
  section?: 'name' | 'username' | 'email' | 'password';
}

// ─── tokens ──────────────────────────────────────────────────────────────

function useTokens() {
  const { theme, isDark } = useTheme();
  return useMemo(
    () => ({
      pageBg: theme.background,
      cardBg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      iconDiscBg: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
      iconDiscBgPressed: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      inputBg: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',
      inputBorder: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      inputBorderFocus: theme.textSecondary,
      inputShadow: isDark ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.04)',
      text: theme.text,
      textSecondary: theme.textSecondary,
      textTertiary: theme.textTertiary,
      bg: theme.background,
      success: '#12a150',
      error: '#d93025',
    }),
    [isDark, theme],
  );
}

type Tokens = ReturnType<typeof useTokens>;

// ─── atoms ───────────────────────────────────────────────────────────────

function IconDisc({
  children,
  tokens,
  size = 36,
}: {
  children: React.ReactNode;
  tokens: Tokens;
  size?: number;
}) {
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: tokens.iconDiscBg,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </View>
  );
}

function PillInput({
  tokens,
  style,
  ...rest
}: TextInputProps & {
  tokens: Tokens;
  style?: StyleProp<ViewStyle>;
}) {
  const [focused, setFocused] = React.useState(false);
  return (
    <View
      style={[
        styles.pillShell,
        {
          backgroundColor: tokens.inputBg,
          borderColor: focused ? tokens.inputBorderFocus : tokens.inputBorder,
          shadowColor: tokens.inputShadow,
          opacity: rest.editable === false ? 0.6 : 1,
        },
        style,
      ]}
    >
      <TextInput
        {...rest}
        placeholderTextColor={tokens.textTertiary}
        onFocus={(event) => {
          setFocused(true);
          rest.onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          rest.onBlur?.(event);
        }}
        style={[styles.pillInput, { color: tokens.text }]}
      />
    </View>
  );
}

function PrimaryButton({
  label,
  onPress,
  tokens,
  loading = false,
  disabled = false,
}: {
  label: string;
  onPress: () => void;
  tokens: Tokens;
  loading?: boolean;
  disabled?: boolean;
}) {
  const haptics = useHaptics();
  const inactive = disabled || loading;
  return (
    <Pressable
      onPress={() => {
        if (inactive) return;
        haptics.trigger('light');
        onPress();
      }}
      disabled={inactive}
      style={({ pressed }) => [
        styles.primaryButton,
        {
          backgroundColor: tokens.text,
          opacity: inactive ? 0.4 : pressed ? 0.88 : 1,
        },
      ]}
    >
      <Text style={[styles.primaryButtonLabel, { color: tokens.bg }]}>
        {loading ? '…' : label}
      </Text>
    </Pressable>
  );
}

function NeutralButton({
  label,
  onPress,
  tokens,
  loading = false,
  disabled = false,
}: {
  label: string;
  onPress: () => void;
  tokens: Tokens;
  loading?: boolean;
  disabled?: boolean;
}) {
  const haptics = useHaptics();
  const inactive = disabled || loading;
  return (
    <Pressable
      onPress={() => {
        if (inactive) return;
        haptics.trigger('light');
        onPress();
      }}
      disabled={inactive}
      style={({ pressed }) => [
        styles.neutralButton,
        {
          backgroundColor: pressed ? tokens.iconDiscBgPressed : tokens.iconDiscBg,
          opacity: inactive ? 0.5 : 1,
        },
      ]}
    >
      <Text style={[styles.neutralButtonLabel, { color: tokens.text }]}>
        {loading ? '…' : label}
      </Text>
    </Pressable>
  );
}

function StatusLine({
  tone,
  message,
  tokens,
}: {
  tone: 'neutral' | 'success' | 'error';
  message: string;
  tokens: Tokens;
}) {
  const color =
    tone === 'success'
      ? tokens.success
      : tone === 'error'
        ? tokens.error
        : tokens.textTertiary;
  return <Text style={[styles.statusText, { color }]}>{message}</Text>;
}

function Field({
  label,
  children,
  tokens,
}: {
  label?: string;
  children: React.ReactNode;
  tokens: Tokens;
}) {
  return (
    <View style={styles.field}>
      {label ? (
        <Text style={[styles.fieldLabel, { color: tokens.textSecondary }]}>
          {label}
        </Text>
      ) : null}
      {children}
    </View>
  );
}

function SectionCard({
  icon,
  title,
  description,
  children,
  tokens,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
  tokens: Tokens;
}) {
  return (
    <View style={[styles.card, { backgroundColor: tokens.cardBg }]}>
      <View style={styles.cardHeader}>
        <IconDisc tokens={tokens}>{icon}</IconDisc>
        <View style={styles.cardHeaderText}>
          <Text style={[styles.cardTitle, { color: tokens.text }]}>{title}</Text>
          {description ? (
            <Text style={[styles.cardDescription, { color: tokens.textTertiary }]}>
              {description}
            </Text>
          ) : null}
        </View>
      </View>
      <View style={styles.cardBody}>{children}</View>
    </View>
  );
}

// ─── component ───────────────────────────────────────────────────────────

export function AccountEditSheet({ embedded = false, section }: AccountEditSheetProps) {
  const tokens = useTokens();
  const { t } = useTranslation('common');
  const editor = useAccountEditor((key, options) =>
    String(t(key, options as any)),
  );

  return (
    <View
      style={[
        styles.screen,
        { backgroundColor: embedded ? 'transparent' : tokens.pageBg },
      ]}
    >
      <KeyboardAvoidingView style={{ flex: 1 }} behavior="padding" automaticOffset>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={[
            styles.content,
            embedded ? styles.contentEmbedded : null,
          ]}
          contentInsetAdjustmentBehavior="automatic"
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {embedded && section !== 'name' && section !== 'username' ? (
            <Text style={[styles.embeddedSubtitle, { color: tokens.textTertiary }]}>
              {t('settings.accountEdit.subtitle')}
            </Text>
          ) : null}

          {/* Name */}
          {(!section || section === 'name') ? <View style={styles.cardBody}>
            <Field tokens={tokens}>
              <PillInput
                tokens={tokens}
                accessibilityLabel={t('settings.accountEdit.nameTitle')}
                value={editor.name}
                onChangeText={editor.setName}
                autoCapitalize="words"
                autoCorrect={false}
                placeholder={t('settings.accountEdit.namePlaceholder')}
                editable={!editor.isIdentityLocked}
              />
            </Field>
            {editor.isIdentityLocked ? (
              <StatusLine
                tone="neutral"
                message={editor.identityLockMessage}
                tokens={tokens}
              />
            ) : null}
            {editor.nameStatus ? (
              <StatusLine
                tone={editor.nameStatus.tone}
                message={editor.nameStatus.message}
                tokens={tokens}
              />
            ) : null}
            <PrimaryButton
              label={t('settings.accountEdit.saveName')}
              tokens={tokens}
              onPress={() => { void editor.saveName(); }}
              loading={editor.isSavingName}
              disabled={
                editor.isIdentityLocked ||
                !editor.name.trim() ||
                editor.isSavingName
              }
            />
          </View> : null}

          {/* Username: plain like Name (no icon disc, no tinted card). It is
              never UAE PASS-locked; only the account name and email are. */}
          {(!section || section === 'username') ? <View style={styles.cardBody}>
            <View style={styles.plainHeader}>
              <Text style={[styles.cardTitle, { color: tokens.text }]}>
                {t('settings.accountEdit.usernameTitle')}
              </Text>
              <Text style={[styles.cardDescription, { color: tokens.textTertiary }]}>
                {t('settings.accountEdit.usernameDescription')}
              </Text>
            </View>
            <Field tokens={tokens}>
              <PillInput
                tokens={tokens}
                accessibilityLabel={t('settings.accountEdit.usernameTitle')}
                value={editor.username}
                onChangeText={(next) => editor.setUsername(next.toLowerCase())}
                autoCapitalize="none"
                autoCorrect={false}
                placeholder={t('settings.accountEdit.usernamePlaceholder')}
                editable={!editor.isUsernameLocked}
              />
            </Field>
            <StatusLine
              tone={editor.usernameStatus.tone}
              message={editor.usernameStatus.message}
              tokens={tokens}
            />
            <PrimaryButton
              label={t('settings.accountEdit.saveUsername')}
              tokens={tokens}
              onPress={() => {
                // The old {handle} link stops working at once, so ask first.
                void confirmUsernameChange(
                  (key, options) => String(t(key, options as any)),
                  { from: editor.currentUsername, to: editor.normalizedUsername },
                ).then((confirmed) => {
                  if (confirmed) void editor.saveUsername();
                });
              }}
              loading={editor.isSavingUsername}
              disabled={
                editor.isUsernameLocked ||
                !editor.canSaveUsername ||
                editor.isSavingUsername
              }
            />
          </View> : null}

          {/* Email */}
          {(!section || section === 'email') ? <SectionCard
            tokens={tokens}
            icon={<Mail size={17} color={tokens.text} strokeWidth={1.8} />}
            title={t('settings.accountEdit.emailTitle')}
            description={
              editor.isIdentityLocked
                ? editor.identityLockMessage
                : t('settings.accountEdit.emailDescription')
            }
          >
            <View style={styles.metaRow}>
              <Text style={[styles.metaLabel, { color: tokens.textTertiary }]}>
                {t('settings.accountEdit.currentEmail')}
              </Text>
              <Text style={[styles.metaValue, { color: tokens.text }]} numberOfLines={1}>
                {editor.currentEmail || 'N/A'}
              </Text>
            </View>
            <Field tokens={tokens}>
              <PillInput
                tokens={tokens}
                value={editor.newEmail}
                onChangeText={editor.setNewEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                placeholder={t('settings.accountEdit.newEmailPlaceholder')}
                editable={!editor.isIdentityLocked}
              />
            </Field>
            <NeutralButton
              label={t('settings.accountEdit.sendOtp')}
              tokens={tokens}
              onPress={() => {
                void editor.requestEmailOtp();
              }}
              loading={editor.isRequestingOtp}
              disabled={
                editor.isIdentityLocked ||
                editor.isRequestingOtp ||
                editor.isVerifyingOtp
              }
            />

            {editor.emailOtpRequested && !editor.isIdentityLocked ? (
              <View style={styles.otpBlock}>
                <Field tokens={tokens}>
                  <PillInput
                    tokens={tokens}
                    value={editor.otp}
                    onChangeText={editor.setOtp}
                    keyboardType="number-pad"
                    autoCapitalize="none"
                    autoCorrect={false}
                    placeholder={t('settings.accountEdit.otpPlaceholder')}
                  />
                </Field>
                <PrimaryButton
                  label={t('settings.accountEdit.verifyEmail')}
                  tokens={tokens}
                  onPress={() => {
                    void editor.verifyEmailOtp();
                  }}
                  loading={editor.isVerifyingOtp}
                  disabled={editor.isVerifyingOtp || editor.isRequestingOtp}
                />
              </View>
            ) : null}

            {editor.emailStatus ? (
              <StatusLine
                tone={editor.emailStatus.tone}
                message={editor.emailStatus.message}
                tokens={tokens}
              />
            ) : null}
          </SectionCard> : null}

          {/* Password */}
          {(!section || section === 'password') ? <SectionCard
            tokens={tokens}
            icon={<Lock size={17} color={tokens.text} strokeWidth={1.8} />}
            title={t('settings.accountEdit.passwordTitle')}
            description={
              editor.isCheckingPasswordMode
                ? t('settings.accountEdit.passwordModeChecking')
                : editor.hasPassword
                  ? t('settings.accountEdit.passwordDescriptionChange')
                  : t('settings.accountEdit.passwordDescriptionSet')
            }
          >
            {editor.hasPassword ? (
              <Field
                tokens={tokens}
                label={t('settings.accountEdit.currentPasswordPlaceholder')}
              >
                <PasswordField
                  value={editor.currentPassword}
                  onChangeText={editor.setCurrentPassword}
                  placeholder={t('settings.accountEdit.currentPasswordPlaceholder')}
                  placeholderTextColor={tokens.textTertiary}
                  textContentType="password"
                  autoComplete="password"
                />
              </Field>
            ) : null}
            <Field
              tokens={tokens}
              label={t('settings.accountEdit.newPasswordPlaceholder')}
            >
              <PasswordField
                value={editor.newPassword}
                onChangeText={editor.setNewPassword}
                placeholder={t('settings.accountEdit.newPasswordPlaceholder')}
                placeholderTextColor={tokens.textTertiary}
                textContentType="newPassword"
                autoComplete="new-password"
                showRules
              />
            </Field>
            <Field
              tokens={tokens}
              label={t('settings.accountEdit.confirmPasswordPlaceholder')}
            >
              <PasswordField
                value={editor.confirmPassword}
                onChangeText={editor.setConfirmPassword}
                placeholder={t('settings.accountEdit.confirmPasswordPlaceholder')}
                placeholderTextColor={tokens.textTertiary}
                textContentType="newPassword"
                autoComplete="new-password"
              />
            </Field>
            <PrimaryButton
              label={
                editor.hasPassword
                  ? t('settings.accountEdit.changePasswordAction')
                  : t('settings.accountEdit.setPasswordAction')
              }
              tokens={tokens}
              onPress={() => {
                void editor.savePassword();
              }}
              loading={editor.isSavingPassword}
              disabled={editor.isSavingPassword || editor.isCheckingPasswordMode}
            />
            {editor.passwordStatus ? (
              <StatusLine
                tone={editor.passwordStatus.tone}
                message={editor.passwordStatus.message}
                tokens={tokens}
              />
            ) : null}
          </SectionCard> : null}
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 48,
    gap: 14,
  },
  contentEmbedded: {
    paddingTop: 8,
  },
  embeddedSubtitle: {
    fontSize: 13,
    lineHeight: 18,
  },
  card: {
    borderRadius: 18,
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  cardHeaderText: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.1,
    lineHeight: 20,
  },
  cardDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  cardBody: {
    gap: 10,
  },
  plainHeader: {
    gap: 4,
    paddingHorizontal: 4,
  },
  field: {
    gap: 6,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '500',
    paddingHorizontal: 4,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 4,
  },
  metaLabel: {
    fontSize: 12.5,
    fontWeight: '500',
  },
  metaValue: {
    fontSize: 13,
    fontWeight: '500',
    flex: 1,
  },
  pillShell: {
    height: 52,
    borderRadius: 9999,
    borderWidth: 1,
    paddingHorizontal: 18,
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 2,
    elevation: 1,
  },
  pillInput: {
    fontSize: 14,
    padding: 0,
    height: '100%',
  },
  primaryButton: {
    height: 48,
    borderRadius: 9999,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  primaryButtonLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  neutralButton: {
    height: 44,
    borderRadius: 9999,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  neutralButtonLabel: {
    fontSize: 13.5,
    fontWeight: '500',
  },
  otpBlock: {
    gap: 10,
    marginTop: 2,
  },
  statusText: {
    fontSize: 12,
    lineHeight: 17,
    paddingHorizontal: 4,
  },
});
